import EvalDealerIntegrations, { CityHelper, OemDealerIntegration, RaioXPluginConfig, RaioXPluginConfigItem, RaioXPluginPersonalization, SuperBidConfiguration, ValueConfig } from "./../../views/private/admin/model/advertisers";
import type { ResolvedPublicationDefaults } from "@app-shared/utils/publication-defaults.utils";
import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { map } from "rxjs/operators";
import { Observable } from "rxjs";
import { BidResult, AlternativeBidRequest, ConditionalOffer } from "./dealer.service";
import { FuelEnum, Vehicle, Color, TransmissionEnum, VehicleSuperBidHistory, StockWebhookHistoryFilter, StockWebhookHistoryItem } from "../model/vehicle";
import { MakeModel, Model, VehicleFilter } from "./open.service";
import { DefaultStatus } from "../model/login";
import { Banner, ExclusiveGroup, SearchDealer } from "@app-model/oem-dealer";
import { DefaultMessage } from "@app/views/private/admin/services/user-manager.service";
import { InspectionReportHistory } from "@app-components/edit-vehicle-pages/import-vehicles/import-vehicles-history/import-vehicles-history.component";
import { InspectionReportRequest } from "@app-components/edit-vehicle-pages/import-vehicles/plates-list/plates-list.component";
import { PageControl } from "@app-model/page-control";
import { SortDirection } from "aws-amplify";
import { PageInfo } from "@app-model/application";
import { ViewerListItem, ViewerListRequest } from "@app-model/viewer-models";
import { ProposalMarginLimitHelper, ProposalMarginToleranceConfig } from "@app-model/proposal-margin.model";

@Injectable({
  providedIn: "root",
})
export class OemDealerService {
  constructor(private http: HttpClient) { }

  saveAuctionConditions(
    vehicleId: string,
    request: ChangeAuctionConditionsRequest
  ): Observable<BidResult> {

    return this.http
      .post(
        `${environment.api}/oemDealer/changeAuctionConditions/${vehicleId}`,
        request
      )
      .pipe(map((response: BidResult) => response));
  }

  saveAuctionConditionsBatch(
    requests: ChangeAuctionConditionsRequest[]
  ): Observable<ChangeAuctionConditionsResponse> {
    return this.http
      .post(
        `${environment.api}/oemDealer/changeAuctionConditions`,
        requests
      )
      .pipe(map((response: ChangeAuctionConditionsResponse) => response));
  }

  getPublicationDefaults(): Observable<OemDealerPublicationDefaultsResponse | null> {
    return this.http
      .get<OemDealerPublicationDefaultsResponse | null>(
        `${environment.api}/oemDealer/publicationDefaults`,
      );
  }

  acceptAlternativeBid(
    request: AlternativeBidRequest,
    vehicleId: string,
    finishAuction: boolean
  ): Observable<BidResult> {
    request.dealerId = request.dealerInfo.id;
    return this.http
      .post(
        `${environment.api}/oemDealer/acceptAlternativeBid/${vehicleId}?finishAuction=${finishAuction}`,
        request
      )
      .pipe(map((response: BidResult) => response));
  }

  makeCounterOffer(vehicleId: string, request: AlternativeBidRequest): Observable<BidResult> {
    request.dealerId = request.dealerInfo.id;
    return this.http
      .post(
        `${environment.api}/oemDealer/makeCounterOffer/${vehicleId}`,
        request
      )
      .pipe(map((response: BidResult) => response));
  }

  cancelCounterOffer(vehicleId: string, request: AlternativeBidRequest): Observable<BidResult> {
    request.dealerId = request.dealerInfo.id;
    return this.http
      .post(
        `${environment.api}/oemDealer/cancelCounterOffer/${vehicleId}`,
        request
      )
      .pipe(map((response: BidResult) => response));
  }

  rejectAlternativeBid(
    request: AlternativeBidRequest,
    vehicleId: string
  ): Observable<BidResult> {
    request.dealerId = request.dealerInfo.id;
    return this.http
      .post(
        `${environment.api}/oemDealer/rejectAlternativeBid/${vehicleId}`,
        request
      )
      .pipe(map((response: BidResult) => response));
  }

  getLocations(): Observable<OemDealerLocation[]> {
    return this.http
      .get(`${environment.api}/oemDealer/locations`)
      .pipe(map((response: OemDealerLocation[]) => response));
  }

  getFipePrice(
    trimId: string,
    modelYear: number,
    fuel: FuelEnum
  ): Observable<number> {
    const queryParams: any = {};
    if (fuel) {
      queryParams.fuel = fuel;
    }
    return this.http
      .get(`${environment.api}/oemDealer/fipePrice/${trimId}/${modelYear}`, {
        params: queryParams,
      })
      .pipe(map((response: number) => response));
  }

  getVehiclesSummary(filter: VehicleFilter): Observable<CastOffVehiclesSummary> {
    return this.http
      .post(`${environment.api}/oemDealer/vehiclesSummary`, filter)
      .pipe(map((response: CastOffVehiclesSummary) => response));
  }

  getEvalDealerOptions(oemDealerId: string): Observable<IdName[]> {
    return this.http
      .get<IdName[]>(`${environment.api}/options/evalDealerOptions/${oemDealerId}`);
  }

  getMakeOptions(segment: string): Observable<MakeModel[]> {
    return this.http
      .get(`${environment.api}/options/makeOptions${segment ? `/${segment}` : ""}`)
      .pipe(map((response: MakeModel[]) => response));
  }

  getModelOptions(makeId: string): Observable<Model[]> {
    return this.http
      .get(`${environment.api}/options/modelOptions${makeId ? `/${makeId}` : ""}`)
      .pipe(map((response: Model[]) => response));
  }

  getTrimOptions(modelId: string): Observable<IdName[]> {
    return this.http
      .get(`${environment.api}/options/trimOptions/${modelId}`)
      .pipe(map((response: IdName[]) => response));
  }

  getEquipmentOptions(segment?: string): Observable<IdName[]> {
    let url = `${environment.api}/options/equipmentOptions`;
    if (segment) {
      url += `?segment=${segment}`;
    }
    return this.http
      .get(url)
      .pipe(map((response: IdName[]) => response));
  }

  saveVehicle(vehicle: Vehicle): Observable<VehicleUpdateResult> {
    return this.http
      .post(`${environment.api}/oemDealer/vehicle/save`, vehicle)
      .pipe(map((response: VehicleUpdateResult) => response));
  }

  addImportedVehicles(
    vehicles: VehicleImportItem[]
  ): Observable<DefaultStatus> {
    return this.http
      .post(`${environment.api}/oemDealer/addVehicles`, vehicles)
      .pipe(map((response: DefaultStatus) => response));
  }

  getDealerInfo(dealerId: string): Observable<DealerInfo> {
    return this.http
      .get(`${environment.api}/oemDealer/dealerInfo/${dealerId}`)
      .pipe(map((response: DealerInfo) => response));
  }

  cancelAuction(
    auctionId: string,
    addDealerBlacklist?: boolean,
    cancellationReason?: string,
    cancellationNote?: string
  ): Observable<void> {
    if (addDealerBlacklist == undefined) {
      addDealerBlacklist = false;
    }

    const body: { cancellationReason: string; cancellationNote?: string } = {
      cancellationReason: cancellationReason!,
    };
    if (cancellationNote) {
      body.cancellationNote = cancellationNote;
    }

    return this.http
      .post(
        `${environment.api}/oemDealer/cancelAuction/${auctionId}?addDealerBlacklist=${addDealerBlacklist}`,
        body
      )
      .pipe(map(() => undefined));
  }

  finishAuction(auctionId: string): Observable<void> {
    return this.http
      .post(
        `${environment.api}/oemDealer/forceFinishAuction/${auctionId}`,
        undefined
      )
      .pipe(map(() => undefined));
  }

  sendPaymentMethod(
    vehicleId: string,
    request?: GeneratePaymentMethodRequest
  ): Observable<DefaultMessage> {
    return this.http
      .post<DefaultMessage>(`${environment.api}/oemDealer/paymentMethod/${vehicleId}`, request ?? {})
      .pipe(map((response: DefaultMessage) => response));
  }

  removeVehicle(vehicleId: string, reason: string): Observable<void> {
    return this.http
      .post(
        `${environment.api}/oemDealer/deleteVehicle/${vehicleId}?reason=${reason}`,
        undefined
      )
      .pipe(map(() => undefined));
  }

  restoreVehicle(vehicleId: string): Observable<DefaultMessage> {
    return this.http
      .post<DefaultMessage>(`${environment.api}/oemDealer/restoreVehicle/${vehicleId}`, undefined);
  }

  checkNumberPlate(numberPlate: string): Observable<VehicleNumberPlateStatus> {
    return this.http
      .get(`${environment.api}/oemDealer/checkNumberPlate/${numberPlate}`)
      .pipe(map((response: VehicleNumberPlateStatus) => response));
  }

  createFromEval(evalVehicleId: string) {
    return this.http
      .get(`${environment.api}/oemDealer/createFromEval/${evalVehicleId}`)
      .pipe(map((response: VehicleUpdateResult) => response));
  }

  getInterestedDealers(vehicleId: string, isFastQuote: boolean) {
    const url = `${environment.api}/oemDealer/${isFastQuote ? 'interestedFastQuoteDealers' : 'interestedDealers'}/${vehicleId}`;
    return this.http
      .get(url)
      .pipe(map((response: InterestedDealers[]) => response));
  }

  getPotentialBuyers(vehicleId: string, filters?: PotentialBuyerFilters): Observable<PotentialBuyerResponse> {
    const params: any = {};
    if (filters?.region) {
      params.region = filters.region;
    }
    if (filters?.modelFilter) {
      params.modelFilter = filters.modelFilter;
    }
    if (filters?.onlyBoughtFromSeller !== undefined) {
      params.onlyBoughtFromSeller = filters.onlyBoughtFromSeller;
    }
    if (filters?.periodDays !== undefined) {
      params.periodDays = filters.periodDays;
    }

    return this.http
      .get<PotentialBuyerResponse>(`${environment.api}/oemDealer/vehicles/${vehicleId}/potential-buyers`, { params })
      .pipe(map((response: PotentialBuyerResponse) => response));
  }

  downloadInterestedDealersInfo(vehicleId: string, isFastQuote: boolean) {
    const url = `${environment.api}/oemDealer/${isFastQuote ? 'interestedFastQuoteDealers' : 'interestedDealers'}CSV/${vehicleId}`;
    return this.http.get(
      url,
      {
        headers: new HttpHeaders({
          Accept: 'text/csv',
        }),
        responseType: 'blob' as 'json',
      }
    );
  }


  generateApiKey(oemDealerId: string, keyName: string): Observable<string> {
    return this.http
      .post(`${environment.api}/oemDealer/generateApiKey/${oemDealerId}`, {}, { params: { 'keyName': keyName }, responseType: 'text' })
      .pipe(map((response: string) => response));
  }

  removeApiKey(oemDealerId: string, keyPrefix: string): Observable<void> {
    return this.http
      .delete(`${environment.api}/oemDealer/deleteApiKey/${oemDealerId}`, { params: { 'keyPrefix': keyPrefix } })
      .pipe(map(() => undefined));
  }



  updateIntegration(integration: OemDealerIntegration): Observable<OemDealerIntegrationUpdateStatus> {
    return this.http
      .post(`${environment.api}/admin/oemDealer/updateIntegration`, integration)
      .pipe(map((response: OemDealerIntegrationUpdateStatus) => response));
  }

  removeIntegration(integrationId: string): Observable<void> {
    return this.http
      .delete(`${environment.api}/admin/oemDealer/removeIntegration/${integrationId}`)
      .pipe(map(() => undefined));
  }

  updateEvalDealerIntegration(integration: EvalDealerIntegrations): Observable<OemDealerIntegrationUpdateStatus> {
    return this.http
      .post(`${environment.api}/admin/oemDealer/updateEvalDealerIntegration`, integration)
      .pipe(map((response: OemDealerIntegrationUpdateStatus) => response));
  }

  getIntegrationTypes(integrationProvider: string): Observable<string[]> {
    return this.http
      .get<string[]>(`${environment.api}/admin/oemDealer/${integrationProvider}/getIntegrationType`);
  }

  authorizeDealer(oemDealerId: string, dealerId: string): Observable<void> {
    return this.http
      .post(`${environment.api}/oemDealer/authorizeDealer/${oemDealerId}/${dealerId}`, {})
      .pipe(map(() => undefined));
  }

  denyDealer(oemDealerId: string, dealerId: string, reason: string): Observable<void> {
    return this.http
      .post(`${environment.api}/oemDealer/denyAuthorization/${oemDealerId}/${dealerId}?reason=${reason}`, {})
      .pipe(map(() => undefined));
  }

  searchPriorityDealersByName(oemDealerId: string, name: string): Observable<DealerInfo[]> {
    return this.http
      .post(`${environment.api}/oemDealer/searchPriorityDealersByName/${oemDealerId}`, {}, { params: { 'name': name } })
      .pipe(map((response: DealerInfo[]) => response));
  }

  addPriorityDealers(oemDealerId: string, dealerIds: string[]): Observable<void> {
    return this.http
      .put(`${environment.api}/oemDealer/addPriorityDealers/${oemDealerId}`, dealerIds)
      .pipe(map(() => undefined));
  }

  searchDealers(data: SearchDealer, consultPending: boolean = false): Observable<DealerInfoSearchResult> {
    let params = new HttpParams();
    if (consultPending) {
      params = params.set('consultPending', 'true');
    }
    return this.http
      .post(`${environment.api}/oemDealer/searchDealers`, data, { params })
      .pipe(map((response: DealerInfoSearchResult | null) => {
        if (!response) {
          return {
            data: [],
            totalElements: 0,
            actualPage: 0,
            numberOfPages: 0,
          } as DealerInfoSearchResult;
        }
        response.data = response.data ?? [];
        return response;
      }));
  }

  directSale(vehicleId: string, request: DirectSaleRequest): Observable<DefaultStatus> {
    return this.http
      .post(`${environment.api}/oemDealer/directSale/${vehicleId}`, request)
      .pipe(map((response: DefaultStatus) => response));
  }

  removePriorityDealer(oemDealerId: string, dealerId: string): Observable<void> {
    return this.http
      .delete(`${environment.api}/oemDealer/removePriorityDealer/${oemDealerId}/${dealerId}`)
      .pipe(map(() => undefined));
  }


  saveRaioXPluginConfig(oemDealerId: string, config: RaioXPluginConfig): Observable<DefaultStatus> {
    return this.http
      .post(`${environment.api}/oemDealer/updateRaioXPluginConfig/${oemDealerId}`, config)
      .pipe(map((response: DefaultStatus) => response));
  }

  saveRaioXPluginPersonalization(oemDealerId: string, personalization: RaioXPluginPersonalization): Observable<DefaultStatus> {
    return this.http
      .post(`${environment.api}/oemDealer/temp/updateRaioXPluginPersonalization/${oemDealerId}`, personalization)
      .pipe(map((response: DefaultStatus) => response));
  }

  disableRaioXPluginConfig(oemDealerId: string): Observable<DefaultStatus> {
    return this.http
      .post(`${environment.api}/oemDealer/disableRaioXPluginConfig/${oemDealerId}`, {})
      .pipe(map((response: DefaultStatus) => response));
  }

  saveExclusiveGroup(data: ExclusiveGroup) {
    return this.http
      .post(`${environment.api}/oemDealer/saveExclusiveGroup`, data)
      .pipe(map(() => undefined));
  }


  saveExclusiveGroupComissionConfig(group: any) {
    const segmentCashbackConfig = {};
    for (const key of group.segmentCashbackConfig.keys()) {
      segmentCashbackConfig[key] = group.segmentCashbackConfig.get(key)
    }

    const segmentComissionConfig = {};
    for (const key of group.segmentComissionConfig.keys()) {
      segmentComissionConfig[key] = group.segmentComissionConfig.get(key)
    }

    return this.http
      .post(`${environment.api}/oemDealer/saveExclusiveGroupComissionConfig/${group.id}`, { segmentCashbackConfig, segmentComissionConfig })
      .pipe(map(() => undefined));
  }

  addDealerExclusiveGroup(id: string, dealers) {
    return this.http
      .put(`${environment.api}/oemDealer/addExclusiveDealers/${id}`, dealers)
      .pipe(map((response: any) => response));
  }

  deleteExclusiveDealer(group: string, dealer: string) {
    return this.http
      .delete(`${environment.api}/oemDealer/removeExclusiveDealer/${group}/${dealer}`)
      .pipe(map((response: any) => response));
  }

  removeDealerFromPendingList(group: string, dealer: string) {
    return this.http
      .delete(`${environment.api}/oemDealer/removeDealerFromPendingList/${group}/${dealer}`)
      .pipe(map((response: any) => response));
  }

  listExclusiveGroups(id: string) {
    return this.http
      .get(`${environment.api}/oemDealer/listExclusiveGroups/${id}`)
      .pipe(map((response: any) => {
        const groups = response ?? [];
        groups.forEach(group => {
          if (group.segmentCashbackConfig) {
            const newMap = new Map<string, ValueConfig>();

            for (const key of Object.keys(group.segmentCashbackConfig)) {
              newMap.set(key, Object.assign(new ValueConfig(), group.segmentCashbackConfig[key]) as ValueConfig);
            }
            group.segmentCashbackConfig = newMap;
          }
          const newMap = new Map<string, ValueConfig>();
          if (group.segmentComissionConfig) {

            for (const key of Object.keys(group.segmentComissionConfig)) {
              newMap.set(key, Object.assign(new ValueConfig(), group.segmentComissionConfig[key]) as ValueConfig);
            }
            group.segmentComissionConfig = newMap;
          }
        });
        return groups;
      }));
  }


  exportRaioXPluginRules(id: string) {
    return this.http
      .get(`${environment.api}/oemDealer/exportRaioXPluginRules/${id}`, {
        headers: new HttpHeaders({
          Accept: 'text/csv',
        }),
        responseType: 'blob' as 'json',
      });
  }

  hasPluginRaioX(oemDealerid: string): Observable<boolean> {
    return this.http.get(`${environment.api}/oemDealer/hasPluginRaioX/${oemDealerid}`)
      .pipe(map((response: boolean) => response));
  }

  saveAudio(audio: Blob, vehicleId: string): Observable<FileUploadResult> {
    const formData = new FormData();
    formData.append('audio', audio);

    return this.http.post<FileUploadResult>(`${environment.api}/oemDealer/vehicle/saveAudio/${vehicleId}`, formData);
  }

  saveMedia(media: Blob, vehicleId: string, reportItem: string): Observable<FileUploadResult> {
    const formData = new FormData();
    formData.append('file', media);
    formData.append('reportItem', reportItem);

    return this.http.post<FileUploadResult>(`${environment.api}/oemDealer/saveMedia/${vehicleId}`, formData);
  }

  uploadVehicleReportPhotos(vehicleId: string, file: File): Observable<FileUploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<FileUploadResult>(`${environment.api}/oemDealer/vehicleReportPhotos/${vehicleId}`, formData);
  }


  downloadExclusiveGroupDealers(groupId: string): Observable<any> {
    const url = `${environment.api}/oemDealer/downloadDealersExclusiveGroup/${groupId}`;
    return this.http.get(
      url,
      {
        headers: new HttpHeaders({
          Accept: 'text/csv',
        }),
        responseType: 'blob' as 'json',
      }
    );
  }

  updateSellingTermsInExclusiveGroup(groupId: string, sellingTerms: string): Observable<DefaultMessage> {
    return this.http.post<DefaultMessage>(`${environment.api}/oemDealer/updateSellingTermsInExclusiveGroup/${groupId}`, sellingTerms);
  }

  getBanners(personalizationId?: string): Observable<Banner[]> {

    const params: any = {};
    if (personalizationId) {
      params.personalizationId = personalizationId;
    }

    return this.http.get<Banner[]>(`${environment.api}/oemDealer/getBanners`, { params: params });
  }

  sortBanners(banners: Banner[]): Observable<DefaultMessage> {
    return this.http.post<DefaultMessage>(`${environment.api}/oemDealer/sortBanners`, banners);
  }


  uploadBanner(img: Blob, isMobile: boolean): Observable<FileUploadStatus> {
    if (!img) {
      return;
    }

    let form = new FormData();
    form.append('file', img);
    form.append('isMobile', isMobile ? 'true' : 'false');

    return this.http.post<FileUploadStatus>(`${environment.api}/oemDealer/uploadBanner`, form);
  }

  saveBanner(banner: Banner): Observable<DefaultMessage> {
    return this.http.post<DefaultMessage>(`${environment.api}/oemDealer/saveBanner`, banner);
  }

  removeBanner(bannerId: string): Observable<DefaultMessage> {
    return this.http.get<DefaultMessage>(`${environment.api}/oemDealer/removeBanner/${bannerId}`);
  }

  addDealersBlacklist(oemDealerId: string, dealersIds: string[], reason: string) {
    return this.http.post<DefaultMessage>(
      `${environment.api}/oemDealer/addDealersBlacklist/${oemDealerId}?reason=${encodeURIComponent(reason)}`,
      dealersIds
    );
  }
  removeDealerBlacklist(oemDealerId: string, dealerId: string, reason: string): Observable<DefaultMessage> {
    return this.http.get<DefaultMessage>(
      `${environment.api}/oemDealer/removeDealerBlacklist/${oemDealerId}/${dealerId}?reason=${encodeURIComponent(reason)}`
    );
  }
  acceptConditionalOffer(vehicleId: string, conditionalOffer: ConditionalOffer): Observable<BidResult> {
    return this.http.post<BidResult>(`${environment.api}/oemDealer/acceptConditionalOffer/${vehicleId}`, conditionalOffer);
  }
  rejectConditionalOffer(vehicleId: string, conditionalOffer: ConditionalOffer): Observable<BidResult> {
    return this.http.post<BidResult>(`${environment.api}/oemDealer/rejectConditionalOffer/${vehicleId}`, conditionalOffer);
  }

  makeCounterConditionalOffer(vehicleId: string, conditionalOffer: ConditionalOffer): Observable<BidResult> {
    return this.http.post<BidResult>(`${environment.api}/oemDealer/makeCounterConditionalOffer/${vehicleId}`, conditionalOffer);
  }

  getAuctionsOffers(type: String): Observable<AuctionAlternativeBidItem[]> {
    return this.http.get<AuctionAlternativeBidItem[]>(`${environment.api}/oemDealer/getAuctionsOffers?type=${type}`);
  }

  getUnitAvailable(oemDealerId: string): Observable<UnitAvailable[]> {
    return this.http.get<UnitAvailable[]>(`${environment.api}/oemDealer/getEvalDealersAvailableToPersonalize/${oemDealerId}`);
  }

  getAdditionalOemDealersAvailable(oemDealerId: string, query: string): Observable<IdName[]> {
    return this.http.get<IdName[]>(`${environment.api}/oemDealer/getAdditionalOemDealersAvailableToPersonalize/${oemDealerId}`, {
      params: { query }
    });
  }

  getAllSuperBidHistories(vehicleId: string): Observable<VehicleSuperBidHistory[]> {
    return this.http.get<VehicleSuperBidHistory[]>(`${environment.api}/oemDealer/getAllSuperBidHistories/${vehicleId}`);
  }

  getStockWebhookHistories(vehicleId: string, filter: StockWebhookHistoryFilter): Observable<PageInfo<StockWebhookHistoryItem>> {
    return this.http.post<PageInfo<StockWebhookHistoryItem>>(`${environment.api}/oemDealer/stockWebhookHistories/${vehicleId}`, filter);
  }

  resendStockWebhookHistory(historyId: string): Observable<StockWebhookHistoryItem> {
    return this.http.post<StockWebhookHistoryItem>(`${environment.api}/oemDealer/resendStockWebhookHistory/${historyId}`, {});
  }

  getSuperBidIntegration(oemDealerId: string): Observable<SuperBidConfiguration[]> {
    return this.http.get<SuperBidConfiguration[]>(`${environment.api}/oemDealer/${oemDealerId}/getSuperBidIntegration`)
      .pipe(
        map((response => {
          response?.forEach(config => {
            let allEventsGroups = [];
            const customGroups = config?.customEventGroups?.map(ce => new IdName(ce.id, ce.name));
            if (customGroups) {
              allEventsGroups = allEventsGroups.concat(customGroups);
            }
            if (config.publicEventGroups) {
              if (config.publicEventGroups.publicGroupId) {
                allEventsGroups.push(new IdName(config.publicEventGroups.publicGroupId, 'Grupo Público'));
              }
              if (config.publicEventGroups.onlyPJGroupId) {
                allEventsGroups.push(new IdName(config.publicEventGroups.onlyPJGroupId, 'Grupo PJ'));
              }
              if (config.publicEventGroups.onlyCNAEGroupId) {
                allEventsGroups.push(new IdName(config.publicEventGroups.onlyCNAEGroupId, 'Grupo CNAE'));
              }


            }
            config.allEventsGroups = allEventsGroups;
          });
          return response;
        }))
      );
  }

  sendAuctionToSuperBid(vehicleId: string, request: any): Observable<DefaultMessage> {
    return this.http.post<DefaultMessage>(`${environment.api}/oemDealer/${vehicleId}/sendAuctionToSuperBid`, request);
  }

  cancelSuperBidOffer(vehicleId: string): Observable<DefaultMessage> {
    return this.http.post<DefaultMessage>(`${environment.api}/oemDealer/${vehicleId}/cancelSuperBidOffer`, {});
  }

  markAsSoldSuperBidOffer(vehicle: string): Observable<DefaultMessage> {
    return this.http.post<DefaultMessage>(`${environment.api}/oemDealer/${vehicle}/soldSuperBidOffer`, {});
  }

  getInspectionReportHistoryByType(
    type: 'INTEGRATION' | 'PDF',
    page = 0,
    pageSize = 10
  ): Observable<{
    data: InspectionReportHistory[],
    totalElements: number,
    numberOfPages: number,
    actualPage: number
  }> {
    const filter: any = {
      type,
      pageControl: {
        ...new PageControl(),
        sortField: "createdAt",
        sortDirection: "DESC",
        page,
        pageSize
      }
    };
    return this.http.post<{
      data: InspectionReportHistory[],
      totalElements: number,
      numberOfPages: number,
      actualPage: number
    }>(
      `${environment.api}/oemDealer/getInspectionReportHistory`,
      filter
    );
  }

  getInspectionReportHistoryById(id: string): Observable<InspectionReportHistory> {
    return this.http
      .get<InspectionReportHistory>(`${environment.api}/oemDealer/getInspectionReportHistory/${id}`);
  }

  fetchInspectionReport(numberPlates: InspectionReportRequest): Observable<DefaultMessage> {
    return this.http
      .post<DefaultMessage>(`${environment.api}/oemDealer/fetchInspectionReport`, numberPlates);
  }

  createVehicleFromReport(
    historyId: string,
    platesToCreate: {
      numberPlate: string,
      oemDealerLocationId?: string,
      initialBid?: number,
      finishValue?: number,
      bidStep?: number
    }[]
  ): Observable<any> {
    return this.http.post<any>(
      `${environment.api}/oemDealer/inspectionReportHistory/${historyId}/createVehicle`,
      { platesToCreate }
    );
  }

  retryCreateVehicleFromReport(
    historyId: string,
    numberPlate: string
  ): Observable<DefaultMessage> {
    return this.http.post<DefaultMessage>(
      `${environment.api}/oemDealer/inspectionReportHistory/${historyId}/${numberPlate}/retryCreateVehicle`,
      {}
    );
  }

  retryInspectionReportHistoryItem(itemId: string): Observable<DefaultMessage> {
    return this.http
      .get<DefaultMessage>(`${environment.api}/oemDealer/retryExtractReportInfo/${itemId}`, {});
  }

  listViewers(request: ViewerListRequest, isFastQuote: boolean = false): Observable<PageInfo<ViewerListItem>> {
    const url = `${environment.api}/oemDealer/${isFastQuote ? 'viewersFastQuote' : 'viewers'}`;
    return this.http
      .post<PageInfo<ViewerListItem>>(url, request)
      .pipe(map((response: PageInfo<ViewerListItem>) => response));
  }

  getProposalMarginToleranceRules(oemDealerId: string): Observable<ProposalMarginToleranceConfig> {
    return this.http.get<ProposalMarginToleranceConfig>(
      `${environment.api}/oemDealer/proposalMarginToleranceRules/${oemDealerId}`
    );
  }

  saveProposalMarginToleranceRules(
    oemDealerId: string,
    body: ProposalMarginToleranceConfig
  ): Observable<DefaultStatus> {
    return this.http.post<DefaultStatus>(
      `${environment.api}/oemDealer/proposalMarginToleranceRules/${oemDealerId}`,
      body
    );
  }

  getProposalMarginLimit(oemDealerId: string): Observable<ProposalMarginLimitHelper> {
    return this.http.get<ProposalMarginLimitHelper>(
      `${environment.api}/oemDealer/proposalMarginLimit/${oemDealerId}`
    );
  }

}

export type OemDealerPublicationTarget =
  | 'ALL'
  | 'ONLY_COMPANIES'
  | 'ONLY_COMPANIES_WITH_CNAE'
  | 'EXCLUSIVE_GROUPS';

export interface OemDealerPublicationDefaultsResponse extends ResolvedPublicationDefaults {
  target: OemDealerPublicationTarget;
}

export class ChangeAuctionConditionsRequest {
  vehicleId?: string;
  locationId: string;
  initialBid: number | null;
  bidStep: number | null;
  finishValue: number | null;
  expirationTime: string | null;
  finishDate?: Date;
  bidOption?: string;
  target: string;
  ignorePersonalizedOffers?: boolean | null;
  dealersExclusiveGroups: string[];
  autoRenewal?: boolean | null;
  allowLeadGeneration?: boolean | null;
  minPersonalizedOfferValue: number | null;
  conditionalOffer?: boolean | null;
  publicationDate?: Date;
  publicationTime?: string;
}

export class ChangeAuctionConditionsResponse {
  success: boolean;
  updatedVehicleIds: string[];
  errors: ChangeAuctionConditionsItemError[];
  updated: number;
  failed: number;
}

export class ChangeAuctionConditionsItemError {
  vehicleId?: string;
  status?: string;
  message?: string;
}

export class OemDealerLocation {
  id?: string;
  name?: string;
  address?: string;
  addressNumber?: string;
  addressComplement?: string;
  zipCode?: string;
  cityId?: string;
  city?: CityHelper;
  oemDealerId?: string;
  regionalAar?: string | null;
  regionalAarId?: string | null;
  regional?: { id: string; name: string } | null;
  makeId?: string;
  division?: string;
  sellingTerms?: string;
}

export class DefaultProductCosts {
  eval: number;
  fullDossier: number;
  susbcription: number;
  raioXPlugin: number;
  ownerInfo: number;
  whatsAppNotification: number;
}

export class VehicleNumberPlateStatus {
  status: string;
  success: boolean;
  evalDetails: Vehicle;
}

export class CastOffVehiclesSummary {
  waitingFirstOffer: number;
  inactive: number;
  fastQuoteWithOffer: number;
  fastQuoteNoOffer: number;
  withOffer: number;
  withPersonalizedOffer: number;
  allSold: number;
  soldToday: number;
  soldWithPendingPayment: number;
  soldLast30Days: number;
  withConditionalOffer: number;
  soldThisMonth: number;
  neverPublished: number;
  inactivated: number;
  published: number;
  fastQuote: number;
  integratedSuperbid: number;
  republishedMultipleTimes: number;
}

export enum VehicleUpdateStatus {
  OK = "ok",
  VEHICLE_NOT_FOUND = "Veículo não encontrado",
  DONT_OWN_VEHICLE = "Veículo não pertence a esta organização",
  MISSING_REQUIRED_INFORMATION = "Preencha todos os campos obrigatórios",
  NUMBERPLATE_ALREADY_EXISTS = "Um veículo com esta placa já está cadastrado.",
  UNKNOWN_ERROR = "Erro desconhecido. Por favor, atualize esta página e tente novamente.",
  NO_LOCATION_DEFINED = "Vendedor sem pátios cadastrados. Por favor cadastre pelo menos um pátio e tente novamente."
}

export enum VehicleRemovalReason {
  SOLD_IN_SHOWROOM = 'Vendido em showroom',
  SOLD_IN_ANOTHER_B2B = 'Vendido em outro canal de venda B2B',
  SOLD_IN_ANOTHER_B2C = 'Vendido em outro canal de venda B2C',
  GAVE_UP_ON_SELLING = 'Decidi não vender mais',
  WENT_BACK_TO_STOCK = 'Voltou para estoque',
  OTHER = 'Outro motivo'

}

export enum CancelSaleReason {
  WRONG_DEALER_OFFER_ACCEPTED = 'Aceitou oferta de comprador errado',
  DEALER_DID_NOT_PAY = 'Comprador não pagou',
  WRONG_TRIM_OR_MODEL = 'Versão/modelo cadastrado errado',
  INCORRECT_LISTED_PRICE = 'Valor anunciado incorreto',
  SOLD_ELSEWHERE = 'Vendido em outro lugar',
  VEHICLE_DIFFERENT_FROM_AD = 'Veículo divergente do anúncio',
  NO_DEALER_RESPONSE = 'Sem retorno do comprador',
  OTHER = 'Outro'
}

export class VehicleRemovalReasonUtils {

  static getExpandedVehicleRemovalReason(reason: string): string {
    switch (reason) {
      case 'CLEAR_DATABASE': return 'Limpeza do banco de dados';
      case 'STOCK_INTEGRATION': return 'Removido via integração de estoque';
      default:
        return VehicleRemovalReason[reason];
    }

  }
}

export enum VehicleUpdatePriceDeleteStatus {
  OK = 'Arquivo importado com sucesso',
  UNKNOWN_ERROR = 'Erro desconhecido. Atualize a página e tente novamente.',
  UNKNOWN_OEM_DEALER = 'Grupo de concessionário desconhecido ou não selecionado.',
  EMPTY_FILE = 'Arquivo vazio',
  INCORRECT_FORMAT = 'Formato incorreto',
  PARTIAL_IMPORT = 'Arquivo importado parcialmente',
  NOTHING_IMPORTED = 'Nenhum dado importado'
}

export interface VehicleUpdateResult {
  id: string;
  success: boolean;
  status: VehicleUpdateStatus;
}

export class IdName {
  id: string;
  name: string;
  fipeId?: string;
  yearsModel?: number[];
  selected?: boolean;
  constructor(id = undefined, name = undefined, fipeId = undefined, yearsModel = undefined) {
    this.id = id;
    this.name = name;
    this.fipeId = fipeId;
    this.yearsModel = yearsModel;
  }
}

export class OemDealerIntegrationUpdateStatus {
  id: string;
  success: boolean;
  status: string;

  static getLabel(status: string): string {
    switch (status) {
      case 'NOT_AUTHORIZED': return 'Você não possui autorização para configurar integrações.';
      case 'INVALID_CREDENTIALS': return 'As credenciais usadas nesta integração estão inválidas. Verifique com o fornecedor e tente novamente.';
      case 'UNKNOWN_ERROR': return 'Ocorreu um erro ao configurar esta integração. Entre em contato com o suporte.';
      default:
        return status;
    }
  }
}

export enum FileUploadStatus {
  OK = "ok",
  VEHICLE_NOT_FOUND = "Veículo não encontrado.",
  DONT_OWN_VEHICLE = "Você não tem permissão para alterar este veículo.",
  UNKNOWN_ERROR = "Erro desconhecido. Atualize a página e tente novamente.",
}

export enum ReportPhotosUploadStatus {
  OK = 'OK',
  INVALID_FILE_TYPE = 'INVALID_FILE_TYPE',
  REPORT_IMAGES_EXTRACTION_IN_PROGRESS = 'REPORT_IMAGES_EXTRACTION_IN_PROGRESS',
  VEHICLE_NOT_FOUND = 'VEHICLE_NOT_FOUND',
  DONT_OWN_VEHICLE = 'DONT_OWN_VEHICLE',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

export interface FileUploadResult {
  status: FileUploadStatus;
  success: boolean;
  url: string;
  resourceId?: string;
  id?: string;
}

export interface RaioXPluginUploadResult {
  status: string;
  success: boolean;
  notFoundMakes: string[];
  notFoundModels: string[];
  newItems: RaioXPluginConfigItem[];
}

export enum VehicleImportStatus {
  OK = "ok",
  UNKNOWN_ERROR = "Erro desconhecido. Atualize a página e tente novamente.",
  UNKNOWN_OEM_DEALER = "Vendedor desconhecido. Escolha apenas um vendedor no seu perfil.",
  EMPTY_FILE = "Arquivo vazio. Por favor, escolha outro arquivo.",
  INCORRECT_FORMAT = "Arquivo com formato inválido. Por favor, siga o formato definido no arquivo de exemplo."
}

export class InterestedDealers {
  city?: string;
  cpfCnpj?: string;
  emails?: string[];
  id?: string;
  name?: string;
  phoneNumbers?: string[];
  whatsappNumber: string;
  stats?: InterestedDealerStatsHelper;
  uf?: string;
}
export class InterestedDealerStatsHelper {
  acceptedPersonalizedOffers?: number;
  bids?: number;
  favorites?: number;
  personalizedOffers?: number;
  rejectedPersonalizedOffers?: number;
  views?: number;
  boughts?: number;
  cancelledBuys?: number;
  lastActionDate?: Date;
}

export class VehicleImportResult {
  status: VehicleImportStatus;
  success: boolean;
  ignoredLines: number;

  confirmed: VehicleImportItem[];
  notConfirmed: VehicleImportItem[];
  notFound: VehicleImportItem[];
  alreadyImported: VehicleImportItem[];

  get newVehicles(): number {
    return (
      (this.confirmed ? this.confirmed.length : 0) +
      (this.notConfirmed ? this.notConfirmed.length : 0) +
      (this.notFound ? this.notFound.length : 0)
    );
  }
}

export class VehicleImportItem {
  rowNumber: number;
  vehicleId: string;
  segment: string;
  selectedMake: MakeModel;
  importedMakeName: string;
  selectedModel: Model;
  importedModelName: string;
  selectedTrim: IdName;
  importedTrimName: string;
  numberPlate: string;
  price: number;
  priceFinishAuction: number;
  internalCode: string;
  color: Color;
  manufactureYear: number;
  modelYear: number;
  fuel: FuelEnum;
  transmission: TransmissionEnum;
  km: number;
  doors: number;
  trimOptions: IdName[];
  trimOptionsInitted: boolean;
  oemDealerLocationId: string;
  stockDate: Date;
  bidStep: number;
  expirationTime: string;
  target: string;
  exclusiveGroup: IdName;
}

export class DealerInfo {
  id: string;
  name: string;
  city: string;
  uf: string;
  phoneNumbers: string[];
  cpfCnpj: string;
  emails: string[];
  email: string;
  lastAccess: Date;
  boughtRecently: number;
  authorized?: boolean;
  bidValue?: number;
  authenticationMode?: string;
  hasLatePayment?: boolean;
  inactive?: boolean;

  get raioXPluginConfig() {
    return null;
  }
}

export class DealerInfoSearchResult {
  actualPage: number;
  data: DealerInfo[];
  numberOfPages: number;
  totalElements: number;
}

export class DealerExclusiveGroupUploadResponse {

  alreadyImported: number;
  removedDealers: number;
  addedDealersActive: number;
  addedNewPrivateDealers: number;
  errors: DealerExclusiveGroupUploadResponseErrorItem[];

}

export class DealerExclusiveGroupUploadResponseErrorItem {
  lineNumber: number;
  errorCode: string;
  name: string;
  cpfCnpj: string;
  data: string;
}

export enum DealerExclusiveGroupUploadResponseErrorCode {
  TYPE_INVALID = 'Tipo inválido (PF/PJ)',
  TYPE_MISSING = 'Tipo é obrigatório',
  INVALID_CPF = 'CPF inválido',
  INVALID_CNPJ = 'CNPJ inválido',
  CPF_CNPJ_MISSING = 'CPF/CNPJ obrigatório',
  NAME_MISSING = 'Nome obrigatório',
  MARITAL_STATUS_MISSING = 'Estado civil obrigatório (PF)',
  MARITAL_STATUS_INVALID = 'Estado civil inválido',
  EMAIL_MISSING = 'Email obrigatório',
  EMAIL_INVALID = 'Email inválido',
  CITY_MISSING = 'Cidade obrigatória',
  UF_MISSING = 'Estado obrigatório',
  CITY_UF_INVALID = 'Cidade/UF inválidos',
  ZIP_CODE_MISSING = 'CEP obrigatório',
  ZIP_CODE_INVALID = 'CEP inválido',
  ADDRESS_MISSING = 'Endereço obrigatório',
  ADDRESS_NUMBER_MISSING = 'Número endereço obrigatório',
  ADDRESS_INVALID = 'Endereço inválido',
  PHONE_NUMBER_MISSING = 'Telefone obrigatório',
  PHONE_NUMBER_INVALID = 'Telefone inválido',
  LEGAL_NAME_MISSING = 'Razão social obrigatória (PJ)',
  CITY_REGISTRATION_MISSING = 'Inscrição municipal obrigatória (PJ)',
  STATE_REGISTRATION_MISSING = 'Inscrição estadual obrigatória (PJ)',
  UNKNOWN_ERROR = 'Erro desconhecido. Entre em contato com o suporte.'
}

export class AuctionAlternativeBidItem {
  fipePrice: number;
  costPrice: number;
  city: number;
  uf: number;
  alternativeBids: AlternativeBidRequest[];
  conditionalOffer: ConditionalOffer;
  vehicle: Vehicle;
  optionSelected: string;
  loading: boolean;
  oemDealerName: string;
  listPrice: number;
  dealerName: string;
  lastPublishedByName: string;
}

export class UnitAvailable {
  id: string;
  name: string
}

export type PotentialBuyerRegion = 'CITY' | 'UF' | 'REGION' | 'COUNTRY';
export type PotentialBuyerModelFilter = 'TRIM_YEAR' | 'TRIM_SIMILAR' | 'MODEL_YEAR' | 'MODEL_SIMILAR';

export interface PotentialBuyerFilters {
  region?: PotentialBuyerRegion;
  modelFilter?: PotentialBuyerModelFilter;
  onlyBoughtFromSeller?: boolean;
  periodDays?: number;
}

export interface PotentialBuyer {
  id: string;
  name: string;
  cpfCnpj: string;
  city: string;
  uf: string;
  phoneNumbers: string[];
  whatsappNumber: string;
  email: string;
  contextTag: string;
  affinityPercent: number;
  lastAccessAt: string;
  totalPurchases: number;
}

export interface PotentialBuyerResponse {
  totalInterested: number;
  averageAffinityLabel: string;
  averageAffinityPercent: number;
  fipePrice: number;
  vehicle: Vehicle;
  buyers: PotentialBuyer[];
}

export interface DirectSaleRequest {
  dealerId: string;
  proposalValue: number;
  mode: 'FINISH_NOW' | 'KEEP_OPEN' | 'SEND_TO_CONDITIONAL';
  keepOpenMinutes?: number;
}

export interface GeneratePaymentMethodRequest {
  paymentMethod?: 'PIX' | 'BANK_SLIP';
}

export function getReportPhotosUploadErrorMessage(status: string): string {
  switch (status) {
    case ReportPhotosUploadStatus.INVALID_FILE_TYPE:
      return 'Apenas arquivos PDF de laudo cautelar são aceitos.';
    case ReportPhotosUploadStatus.REPORT_IMAGES_EXTRACTION_IN_PROGRESS:
      return 'Extração de fotos do laudo em andamento.';
    case ReportPhotosUploadStatus.VEHICLE_NOT_FOUND:
      return 'Veículo não encontrado.';
    case ReportPhotosUploadStatus.DONT_OWN_VEHICLE:
      return 'Você não tem permissão para alterar este veículo.';
    default:
      return 'Erro ao enviar o laudo. Tente novamente.';
  }

}  